/**
 * Context Command Autocomplete Component
 * Dropdown for @context command - adds documents to chat context
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { searchApi } from '../../services/api';
import { useAI, ContextDocument } from '../../contexts/AIContext';
import { logger } from '../../utils/logger';
import { useAutocompleteKeyboard } from '../../hooks/useAutocompleteKeyboard';

interface SearchResult {
  id: string;
  documentId?: string;
  title: string;
  standardNumber?: string;
  type: 'standard' | 'company' | 'project';
  score: number;
  snippet?: string;
}

interface ContextCommandAutocompleteProps {
  query: string;
  onClose: () => void;
  position?: { top: number; left: number };
}

/**
 * Extract standard number from snippet text
 */
function extractStandardFromSnippet(snippet?: string): string | null {
  if (!snippet) return null;
  
  const patterns = [
    /\b(ARP\s*\d+[A-Z]?)\b/i,
    /\b(DO-?\d+[A-Z]?)\b/i,
    /\b(AS\d+[A-Z]?)\b/i,
    /\b(MIL-STD-\d+[A-Z]?)\b/i,
    /\b(ED-\d+[A-Z]?)\b/i,
    /\b(RTCA\/DO-?\d+[A-Z]?)\b/i,
    /\b(SAE\s+[A-Z]+\s*\d+[A-Z]?)\b/i,
    /\b(AC\s*\d+-\d+[A-Z]?)\b/i,
  ];
  
  for (const pattern of patterns) {
    const match = snippet.match(pattern);
    if (match) {
      return match[1].replace(/\s+/g, '').toUpperCase();
    }
  }
  
  return null;
}

export default function ContextCommandAutocomplete({
  query,
  onClose,
  position,
}: ContextCommandAutocompleteProps) {
  const { addContextDocument, contextDocuments, openedDocuments } = useAI();
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Get open tabs that aren't already in context
  // Check both id AND documentNumber for deduplication
  const availableTabs = openedDocuments.filter(
    doc => doc.tabType === 'document' && !contextDocuments.some(c => 
      c.id === doc.id || 
      (doc.documentNumber && c.documentNumber && c.documentNumber === doc.documentNumber)
    )
  );

  // Debounced search
  useEffect(() => {
    if (!query || query.trim().length === 0) {
      setResults([]);
      return;
    }

    setLoading(true);

    const timeoutId = setTimeout(async () => {
      try {
        const response = await searchApi.searchAllDocuments(query.trim(), 8);
        const contextIds = new Set(contextDocuments.map(c => c.id));
        const contextDocNumbers = new Set(contextDocuments.map(c => c.documentNumber).filter(Boolean));
        interface RawSearchResult {
          id?: string;
          documentId?: string;
          documentNumber?: string;
          standardNumber?: string;
          title?: string;
          type?: 'standard' | 'company' | 'project';
          score?: number;
          snippet?: string;
        }
        const filtered = (response.results || [])
          .filter((r: RawSearchResult) => {
            const docId = r.id || r.documentId;
            const docNumber = r.documentNumber || r.documentId;
            return !contextIds.has(docId!) && !contextDocNumbers.has(docNumber);
          })
          .map((r: RawSearchResult) => ({
            id: r.documentNumber || r.documentId || r.id,
            documentId: r.documentId,
            title: r.title,
            standardNumber: r.documentNumber || r.standardNumber,
            type: r.type as 'standard' | 'company' | 'project',
            score: r.score,
            snippet: r.snippet,
          }));
        setResults(filtered);
      } catch (error) {
        logger.error('Search failed:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query, contextDocuments]);

  // Build options list: open tabs first, then search results
  const allOptions: Array<{ type: 'tab' | 'result'; data: { id: string; documentNumber?: string; title: string; type?: string } }> = [
    ...availableTabs.map(tab => ({ type: 'tab' as const, data: tab })),
    ...results.map(r => ({ type: 'result' as const, data: r })),
  ];

  const handleSelect = useCallback((option: typeof allOptions[0]) => {
    if (option.type === 'tab') {
      const tab = option.data;
      const contextDoc: ContextDocument = {
        id: tab.id,
        title: tab.title,
        type: 'document',
        documentNumber: tab.documentNumber,
        tabId: tab.tabId,
      };
      addContextDocument(contextDoc);
    } else {
      const result = option.data as SearchResult;
      const standardNum = result.standardNumber || extractStandardFromSnippet(result.snippet);
      const displayTitle = standardNum 
        ? (result.title && !result.title.startsWith('ref_') 
            ? `${standardNum} - ${result.title}` 
            : standardNum)
        : (result.title && !result.title.startsWith('ref_') 
            ? result.title 
            : 'Reference Document');
      
      const contextDoc: ContextDocument = {
        id: result.id,
        title: displayTitle,
        type: result.type === 'standard' ? 'reference' : result.type,
        documentNumber: result.id,
      };
      addContextDocument(contextDoc);
    }
    onClose();
  }, [allOptions, addContextDocument, onClose]);

  const handleKeySelect = useCallback((index: number) => {
    if (allOptions[index]) {
      handleSelect(allOptions[index]);
    }
  }, [allOptions, handleSelect]);

  const { selectedIndex } = useAutocompleteKeyboard({
    itemCount: allOptions.length,
    onSelect: handleKeySelect,
    onClose,
    wrapAround: false,
    scrollIntoView: false,
    resetDeps: [query],
  });

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'standard':
      case 'reference':
        return '📚';
      case 'company':
        return '🏢';
      case 'project':
        return '📁';
      default:
        return '📄';
    }
  };

  return (
    <div
      ref={containerRef}
      className="absolute z-50 w-[450px] max-h-[400px] overflow-y-auto bg-card border border-border rounded-lg shadow-xl"
      // eslint-disable-next-line no-restricted-syntax -- Dynamic position based on prop
      style={position ? { bottom: position.top, left: position.left } : { bottom: '100%', left: 0, marginBottom: '8px' }}
    >
      {/* Header */}
      <div className="sticky top-0 bg-primary text-primary-foreground px-4 py-2 border-b border-primary">
        <div className="flex items-center gap-2">
          <span className="text-lg">📎</span>
          <span className="font-medium">Add to Context</span>
        </div>
        <p className="text-xs text-link mt-1">
          Add documents to include in your conversation
        </p>
      </div>

      {/* Open Tabs Section */}
      {availableTabs.length > 0 && (
        <div>
          <div className="px-3 py-1.5 bg-muted text-xs font-medium text-muted-foreground uppercase tracking-wide">
            📑 Open Tabs
          </div>
          {availableTabs.map((tab, index) => (
            <div
              key={tab.id}
              onClick={() => handleSelect({ type: 'tab', data: tab })}
              className={`px-4 py-2 cursor-pointer flex items-center gap-3 ${
                index === selectedIndex
                  ? 'bg-info-light'
                  : 'hover:bg-muted'
              }`}
            >
              <span className="text-lg">{getTypeIcon(tab.type || 'document')}</span>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-foreground truncate">
                  {tab.title}
                </div>
                {tab.documentNumber && (
                  <div className="text-xs text-muted-foreground font-mono">
                    {tab.documentNumber}
                  </div>
                )}
              </div>
              <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded">
                Tab {tab.tabId}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Search Results Section */}
      {query && (
        <div>
          <div className="px-3 py-1.5 bg-muted text-xs font-medium text-muted-foreground uppercase tracking-wide">
            🔍 Search Results {loading && '...'}
          </div>
          {results.length > 0 ? (
            results.map((result, index) => {
              const actualIndex = availableTabs.length + index;
              const standardNum = result.standardNumber || extractStandardFromSnippet(result.snippet);
              const displayTitle = standardNum 
                ? (result.title && !result.title.startsWith('ref_') 
                    ? `${standardNum} - ${result.title}` 
                    : standardNum)
                : (result.title && !result.title.startsWith('ref_') 
                    ? result.title 
                    : 'Reference Document');
              
              return (
                <div
                  key={result.id}
                  onClick={() => handleSelect({ type: 'result', data: result })}
                  className={`px-4 py-2 cursor-pointer flex items-center gap-3 ${
                    actualIndex === selectedIndex
                      ? 'bg-info-light'
                      : 'hover:bg-muted'
                  }`}
                >
                  <span className="text-lg">{getTypeIcon(result.type)}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground truncate">
                      {displayTitle}
                    </div>
                    {result.snippet && (
                      <div className="text-xs text-muted-foreground truncate">
                        {result.snippet}
                      </div>
                    )}
                  </div>
                  {result.score >= 0.8 && (
                    <span className="text-xs px-1.5 py-0.5 bg-success-light text-success rounded">
                      High match
                    </span>
                  )}
                </div>
              );
            })
          ) : !loading && query.length > 0 ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">
              No documents found for "{query}"
            </div>
          ) : null}
        </div>
      )}

      {/* Empty state */}
      {!query && availableTabs.length === 0 && (
        <div className="px-4 py-4 text-center text-muted-foreground text-sm">
          <p>Type to search for documents to add to context</p>
        </div>
      )}

      {/* Footer hint */}
      <div className="sticky bottom-0 px-3 py-2 bg-muted border-t border-border text-xs text-muted-foreground flex items-center justify-between">
        <div>
          <kbd className="px-1.5 py-0.5 bg-muted rounded">↑↓</kbd> Navigate
          <kbd className="px-1.5 py-0.5 bg-muted rounded ml-2">Enter</kbd> Add
          <kbd className="px-1.5 py-0.5 bg-muted rounded ml-2">Esc</kbd> Cancel
        </div>
        <span>{contextDocuments.length} in context</span>
      </div>
    </div>
  );
}
